// src/pages/Settings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, storage, auth } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [admins, setAdmins] = useState([]);
  const [currentTime, setCurrentTime] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // General Settings
  const [generalData, setGeneralData] = useState({
    collegeName: '',
    tagline: '',
    marqueeText: '',
    phone: '',
    email: '',
    address: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    youtube: '',
    directorName: '',
    directorMessage: '',
    logoUrl: '',
    heroPhotoUrl: '',
    directorPhotoUrl: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [directorPhotoFile, setDirectorPhotoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [heroPreview, setHeroPreview] = useState('');
  const [directorPhotoPreview, setDirectorPhotoPreview] = useState('');

  // About Settings
  const [aboutData, setAboutData] = useState({
    title: '',
    content: '',
    mission: '',
    vision: '',
    bannerUrl: ''
  });
  const [aboutBannerFile, setAboutBannerFile] = useState(null);
  const [aboutBannerPreview, setAboutBannerPreview] = useState('');

  // Contact Settings
  const [contactData, setContactData] = useState({
    pageTitle: '',
    description: '',
    mapUrl: '',
    weekdayHours: '',
    saturdayHours: '',
    sundayHours: '',
    emergencyPhone: '',
    ambulanceContact: '',
    securityContact: '',
    bannerUrl: ''
  });
  const [contactBannerFile, setContactBannerFile] = useState(null);
  const [contactBannerPreview, setContactBannerPreview] = useState('');

  // Admin Form
  const [adminForm, setAdminForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin'
  });

  // Load all data
  const loadGeneralSettings = useCallback(async () => {
    try {
      const docRef = doc(db, 'settings', 'college_info');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setGeneralData({
          collegeName: data.collegeName || '',
          tagline: data.tagline || '',
          marqueeText: data.marqueeText || '',
          phone: data.phone || '',
          email: data.email || '',
          address: data.address || '',
          facebook: data.facebook || '',
          instagram: data.instagram || '',
          linkedin: data.linkedin || '',
          youtube: data.youtube || '',
          directorName: data.directorName || '',
          directorMessage: data.directorMessage || '',
          logoUrl: data.logoUrl || '',
          heroPhotoUrl: data.heroPhotoUrl || '',
          directorPhotoUrl: data.directorPhotoUrl || ''
        });
        if (data.logoUrl) setLogoPreview(data.logoUrl);
        if (data.heroPhotoUrl) setHeroPreview(data.heroPhotoUrl);
        if (data.directorPhotoUrl) setDirectorPhotoPreview(data.directorPhotoUrl);
      }
    } catch (error) {
      console.error('Error loading general settings:', error);
    }
  }, []);

  const loadAboutSettings = useCallback(async () => {
    try {
      const docRef = doc(db, 'pages', 'about');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setAboutData({
          title: data.title || '',
          content: data.content || '',
          mission: data.mission || '',
          vision: data.vision || '',
          bannerUrl: data.bannerUrl || ''
        });
        if (data.bannerUrl) setAboutBannerPreview(data.bannerUrl);
      }
    } catch (error) {
      console.error('Error loading about settings:', error);
    }
  }, []);

  const loadContactSettings = useCallback(async () => {
    try {
      const docRef = doc(db, 'pages', 'contact');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setContactData({
          pageTitle: data.pageTitle || '',
          description: data.description || '',
          mapUrl: data.mapUrl || '',
          weekdayHours: data.weekdayHours || '',
          saturdayHours: data.saturdayHours || '',
          sundayHours: data.sundayHours || '',
          emergencyPhone: data.emergencyPhone || '',
          ambulanceContact: data.ambulanceContact || '',
          securityContact: data.securityContact || '',
          bannerUrl: data.bannerUrl || ''
        });
        if (data.bannerUrl) setContactBannerPreview(data.bannerUrl);
      }
    } catch (error) {
      console.error('Error loading contact settings:', error);
    }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'admins'));
      const adminList = [];
      snapshot.forEach((doc) => {
        adminList.push({ id: doc.id, ...doc.data() });
      });
      setAdmins(adminList);
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadGeneralSettings(),
        loadAboutSettings(),
        loadContactSettings(),
        loadAdmins()
      ]);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
    setLoading(false);
  }, [loadGeneralSettings, loadAboutSettings, loadContactSettings, loadAdmins]);

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    loadAllData();

    return () => clearInterval(clockInterval);
  }, [loadAllData]);

  const handleFileChange = (e, setter, previewSetter) => {
    const file = e.target.files[0];
    if (file) {
      setter(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        previewSetter(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadFile = async (file, path) => {
    if (!file) return null;
    const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        }
      );
    });
  };

  const saveGeneralSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setIsUploading(true);
    try {
      const logoUrl = await uploadFile(logoFile, 'settings/logo') || generalData.logoUrl;
      const heroUrl = await uploadFile(heroFile, 'settings/hero') || generalData.heroPhotoUrl;
      const directorPhotoUrl = await uploadFile(directorPhotoFile, 'settings/director') || generalData.directorPhotoUrl;

      const data = {
        ...generalData,
        logoUrl,
        heroPhotoUrl: heroUrl,
        directorPhotoUrl,
        updatedAt: new Date()
      };

      await setDoc(doc(db, 'settings', 'college_info'), data, { merge: true });
      alert('General settings saved successfully!');
      await loadGeneralSettings();
      setLogoFile(null);
      setHeroFile(null);
      setDirectorPhotoFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error saving general settings:', error);
      alert('Error saving general settings: ' + error.message);
    } finally {
      setSaving(false);
      setIsUploading(false);
    }
  };

  const saveAboutSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setIsUploading(true);
    try {
      const bannerUrl = await uploadFile(aboutBannerFile, 'pages/about') || aboutData.bannerUrl;
      const data = {
        ...aboutData,
        bannerUrl,
        updatedAt: new Date()
      };
      await setDoc(doc(db, 'pages', 'about'), data, { merge: true });
      alert('About settings saved successfully!');
      await loadAboutSettings();
      setAboutBannerFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error saving about settings:', error);
      alert('Error saving about settings: ' + error.message);
    } finally {
      setSaving(false);
      setIsUploading(false);
    }
  };

  const saveContactSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setIsUploading(true);
    try {
      const bannerUrl = await uploadFile(contactBannerFile, 'pages/contact') || contactData.bannerUrl;
      const data = {
        ...contactData,
        bannerUrl,
        updatedAt: new Date()
      };
      await setDoc(doc(db, 'pages', 'contact'), data, { merge: true });
      alert('Contact settings saved successfully!');
      await loadContactSettings();
      setContactBannerFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error saving contact settings:', error);
      alert('Error saving contact settings: ' + error.message);
    } finally {
      setSaving(false);
      setIsUploading(false);
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    const { name, email, password, confirmPassword, role } = adminForm;
    
    if (!name || !email || !password) {
      alert('Please fill all fields');
      return;
    }
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'admins', userCred.user.uid), {
        name,
        email,
        role,
        active: true,
        createdAt: new Date()
      });
      alert('Admin added successfully!');
      setAdminForm({ name: '', email: '', password: '', confirmPassword: '', role: 'admin' });
      await loadAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      alert('Error adding admin: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    try {
      await deleteDoc(doc(db, 'admins', id));
      alert('Admin deleted successfully!');
      await loadAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('Error deleting admin: ' + error.message);
    }
  };

  const resetGeneral = () => {
    if (window.confirm('Revert all changes?')) {
      setLogoFile(null);
      setHeroFile(null);
      setDirectorPhotoFile(null);
      loadGeneralSettings();
      alert('Form reset');
    }
  };

  const resetAbout = () => {
    if (window.confirm('Revert all changes?')) {
      setAboutBannerFile(null);
      loadAboutSettings();
      alert('Form reset');
    }
  };

  const resetContact = () => {
    if (window.confirm('Revert all changes?')) {
      setContactBannerFile(null);
      loadContactSettings();
      alert('Form reset');
    }
  };

  // Styles
  const styles = `
    .settings-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .settings-tabs {
      display: flex;
      gap: 12px;
      margin-bottom: 32px;
      flex-wrap: wrap;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
      padding-bottom: 16px;
    }

    .tab-btn {
      background: rgba(255, 255, 255, 0.4);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 2px solid rgba(14, 165, 233, 0.2);
      padding: 12px 28px;
      border-radius: 40px;
      font-weight: 700;
      font-size: 0.9rem;
      color: #334155;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .tab-btn:hover {
      background: rgba(255, 255, 255, 0.7);
      border-color: #0ea5e9;
      transform: translateY(-2px);
    }

    .tab-btn.active {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border-color: transparent;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
      transform: translateY(-2px);
    }

    .tab-panel {
      display: none;
      animation: fadeIn 0.4s ease;
    }

    .tab-panel.active {
      display: block;
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .settings-card-panel {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      padding: 32px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      margin-bottom: 30px;
    }

    .settings-card-panel:hover {
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .panel-section-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .panel-section-header i {
      font-size: 22px;
      color: #0ea5e9;
    }

    .panel-section-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.1rem;
      font-weight: 700;
      color: #0f172a;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .form-inputs-matrix {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
      gap: 20px;
    }

    .form-input-group {
      margin-bottom: 4px;
    }

    .form-input-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-input-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-input-group input,
    .form-input-group textarea,
    .form-input-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-input-group input:focus,
    .form-input-group textarea:focus,
    .form-input-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-input-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .form-footer-actions-row {
      display: flex;
      justify-content: flex-end;
      gap: 16px;
      margin-top: 24px;
      padding-top: 20px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .image-preview-frame {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
    }

    .image-preview-frame img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      display: block;
    }

    .info-tip-container-box {
      background: rgba(14, 165, 233, 0.08);
      border: 1px solid rgba(14, 165, 233, 0.2);
      border-radius: 8px;
      padding: 12px 16px;
      margin-top: 12px;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .info-tip-container-box i {
      font-size: 16px;
      color: #0ea5e9;
    }

    .info-tip-container-box p {
      color: #334155;
      font-size: 0.8rem;
      font-weight: 600;
      line-height: 1.4;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .btn-action-trigger {
      padding: 12px 28px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover:not(:disabled) {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover:not(:disabled) {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-row-action-reset {
      padding: 12px 28px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-row-action-reset:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none !important;
    }

    .admin-table {
      width: 100%;
      border-collapse: collapse;
    }

    .admin-table th,
    .admin-table td {
      padding: 14px;
      text-align: left;
      border-bottom: 1px solid rgba(14, 165, 233, 0.1);
    }

    .admin-table th {
      background: rgba(14, 165, 233, 0.08);
      font-weight: 700;
      color: #0f172a;
    }

    .admin-table tr:hover {
      background: rgba(255, 255, 255, 0.3);
    }

    .admin-table .role-badge {
      background: #0ea5e9;
      padding: 4px 12px;
      border-radius: 20px;
      color: white;
      font-size: 0.7rem;
      font-weight: 700;
      display: inline-block;
    }

    .delete-admin-btn {
      background: #f43f5e;
      border: none;
      padding: 6px 14px;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.75rem;
      transition: all 0.2s;
    }

    .delete-admin-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(244, 63, 94, 0.3);
    }

    @media (max-width: 768px) {
      .form-inputs-matrix {
        grid-template-columns: 1fr;
      }
      .form-footer-actions-row {
        flex-direction: column;
      }
      .form-footer-actions-row button {
        width: 100%;
        justify-content: center;
      }
      .settings-tabs {
        gap: 8px;
      }
      .tab-btn {
        padding: 8px 16px;
        font-size: 0.8rem;
        flex: 1;
        justify-content: center;
      }
      .admin-table {
        display: block;
        overflow-x: auto;
      }
    }
  `;

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="settings-page">
        <div className="welcome-hero">
          <h1>⚙️ Website Settings</h1>
          <p>Configure institutional metadata, core contact indices, messaging anchors, and global media logos.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button 
            className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <i className="fas fa-university"></i> General
          </button>
          <button 
            className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            <i className="fas fa-info-circle"></i> About
          </button>
          <button 
            className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            <i className="fas fa-envelope"></i> Contact
          </button>
          <button 
            className={`tab-btn ${activeTab === 'admins' ? 'active' : ''}`}
            onClick={() => setActiveTab('admins')}
          >
            <i className="fas fa-users"></i> Admins
          </button>
        </div>

        {/* General Settings */}
        <div className={`tab-panel ${activeTab === 'general' ? 'active' : ''}`}>
          <form onSubmit={saveGeneralSettings}>
            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-university"></i>
                <h2>Basic Information</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label>College Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={generalData.collegeName}
                    onChange={(e) => setGeneralData({ ...generalData, collegeName: e.target.value })}
                    placeholder="Enter college name"
                    required
                  />
                </div>
                <div className="form-input-group">
                  <label>Tagline / Slogan</label>
                  <input
                    type="text"
                    value={generalData.tagline}
                    onChange={(e) => setGeneralData({ ...generalData, tagline: e.target.value })}
                    placeholder="Enter college tagline"
                  />
                </div>
                <div className="form-input-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Marquee Announcement Text</label>
                  <textarea
                    value={generalData.marqueeText}
                    onChange={(e) => setGeneralData({ ...generalData, marqueeText: e.target.value })}
                    rows="2"
                    placeholder="Enter announcement text that will scroll on website"
                  />
                </div>
              </div>
            </div>

            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-address-card"></i>
                <h2>Contact Information</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={generalData.phone}
                    onChange={(e) => setGeneralData({ ...generalData, phone: e.target.value })}
                    placeholder="+91 1234567890"
                  />
                </div>
                <div className="form-input-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={generalData.email}
                    onChange={(e) => setGeneralData({ ...generalData, email: e.target.value })}
                    placeholder="info@college.edu"
                  />
                </div>
                <div className="form-input-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Address</label>
                  <textarea
                    value={generalData.address}
                    onChange={(e) => setGeneralData({ ...generalData, address: e.target.value })}
                    rows="2"
                    placeholder="Enter complete college address"
                  />
                </div>
              </div>
            </div>

            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-share-alt"></i>
                <h2>Social Media Links</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label><i className="fab fa-facebook" style={{ color: '#1877f2' }}></i> Facebook</label>
                  <input
                    type="url"
                    value={generalData.facebook}
                    onChange={(e) => setGeneralData({ ...generalData, facebook: e.target.value })}
                    placeholder="https://facebook.com/college"
                  />
                </div>
                <div className="form-input-group">
                  <label><i className="fab fa-instagram" style={{ color: '#e1306c' }}></i> Instagram</label>
                  <input
                    type="url"
                    value={generalData.instagram}
                    onChange={(e) => setGeneralData({ ...generalData, instagram: e.target.value })}
                    placeholder="https://instagram.com/college"
                  />
                </div>
                <div className="form-input-group">
                  <label><i className="fab fa-linkedin" style={{ color: '#0077b5' }}></i> LinkedIn</label>
                  <input
                    type="url"
                    value={generalData.linkedin}
                    onChange={(e) => setGeneralData({ ...generalData, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/school/college"
                  />
                </div>
                <div className="form-input-group">
                  <label><i className="fab fa-youtube" style={{ color: '#ff0000' }}></i> YouTube</label>
                  <input
                    type="url"
                    value={generalData.youtube}
                    onChange={(e) => setGeneralData({ ...generalData, youtube: e.target.value })}
                    placeholder="https://youtube.com/college"
                  />
                </div>
              </div>
            </div>

            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-user-tie"></i>
                <h2>Director's Message</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label>Director Name</label>
                  <input
                    type="text"
                    value={generalData.directorName}
                    onChange={(e) => setGeneralData({ ...generalData, directorName: e.target.value })}
                    placeholder="Dr. John Doe"
                  />
                </div>
                <div className="form-input-group">
                  <label>Director Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setDirectorPhotoFile, setDirectorPhotoPreview)}
                  />
                  {directorPhotoPreview && (
                    <div className="image-preview-frame">
                      <img src={directorPhotoPreview} alt="Director" />
                    </div>
                  )}
                </div>
                <div className="form-input-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Director Message</label>
                  <textarea
                    value={generalData.directorMessage}
                    onChange={(e) => setGeneralData({ ...generalData, directorMessage: e.target.value })}
                    rows="4"
                    placeholder="Enter director's message to students and parents..."
                  />
                </div>
              </div>
            </div>

            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-images"></i>
                <h2>College Media</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label>College Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setLogoFile, setLogoPreview)}
                  />
                  {logoPreview && (
                    <div className="image-preview-frame">
                      <img src={logoPreview} alt="Logo" />
                    </div>
                  )}
                  <div className="info-tip-container-box">
                    <i className="fas fa-info-circle"></i>
                    <p>Recommended size: 200x200px. Format: PNG, JPG, JPEG</p>
                  </div>
                </div>
                <div className="form-input-group">
                  <label>Hero / Banner Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setHeroFile, setHeroPreview)}
                  />
                  {heroPreview && (
                    <div className="image-preview-frame">
                      <img src={heroPreview} alt="Hero" />
                    </div>
                  )}
                  <div className="info-tip-container-box">
                    <i className="fas fa-info-circle"></i>
                    <p>Recommended size: 1920x600px. Format: PNG, JPG, JPEG</p>
                  </div>
                </div>
              </div>
            </div>

            {isUploading && uploadProgress > 0 && (
              <div className="upload-progress">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}

            <div className="form-footer-actions-row">
              <button type="button" className="btn-row-action-reset" onClick={resetGeneral}>
                <i className="fas fa-undo"></i> Reset
              </button>
              <button type="submit" className="btn-action-trigger btn-blue" disabled={saving || isUploading}>
                <i className="fas fa-save"></i> {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>

        {/* About Settings */}
        <div className={`tab-panel ${activeTab === 'about' ? 'active' : ''}`}>
          <form onSubmit={saveAboutSettings}>
            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-info-circle"></i>
                <h2>About Us Content</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Page Title</label>
                  <input
                    type="text"
                    value={aboutData.title}
                    onChange={(e) => setAboutData({ ...aboutData, title: e.target.value })}
                    placeholder="About Dolphin Institute"
                  />
                </div>
                <div className="form-input-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Main Content</label>
                  <textarea
                    value={aboutData.content}
                    onChange={(e) => setAboutData({ ...aboutData, content: e.target.value })}
                    rows="6"
                    placeholder="Write about the institute, mission, vision, history..."
                  />
                </div>
                <div className="form-input-group">
                  <label>Mission Statement</label>
                  <textarea
                    value={aboutData.mission}
                    onChange={(e) => setAboutData({ ...aboutData, mission: e.target.value })}
                    rows="3"
                    placeholder="Our mission..."
                  />
                </div>
                <div className="form-input-group">
                  <label>Vision Statement</label>
                  <textarea
                    value={aboutData.vision}
                    onChange={(e) => setAboutData({ ...aboutData, vision: e.target.value })}
                    rows="3"
                    placeholder="Our vision..."
                  />
                </div>
                <div className="form-input-group">
                  <label>About Banner Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setAboutBannerFile, setAboutBannerPreview)}
                  />
                  {aboutBannerPreview && (
                    <div className="image-preview-frame">
                      <img src={aboutBannerPreview} alt="About Banner" />
                    </div>
                  )}
                </div>
              </div>

              {isUploading && uploadProgress > 0 && (
                <div className="upload-progress">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                </div>
              )}

              <div className="form-footer-actions-row">
                <button type="button" className="btn-row-action-reset" onClick={resetAbout}>
                  <i className="fas fa-undo"></i> Reset
                </button>
                <button type="submit" className="btn-action-trigger btn-blue" disabled={saving || isUploading}>
                  <i className="fas fa-save"></i> {saving ? 'Saving...' : 'Save About Us'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Contact Settings */}
        <div className={`tab-panel ${activeTab === 'contact' ? 'active' : ''}`}>
          <form onSubmit={saveContactSettings}>
            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-envelope"></i>
                <h2>Contact Page Content</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Page Title</label>
                  <input
                    type="text"
                    value={contactData.pageTitle}
                    onChange={(e) => setContactData({ ...contactData, pageTitle: e.target.value })}
                    placeholder="Get in Touch"
                  />
                </div>
                <div className="form-input-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Page Description</label>
                  <textarea
                    value={contactData.description}
                    onChange={(e) => setContactData({ ...contactData, description: e.target.value })}
                    rows="3"
                    placeholder="We'd love to hear from you..."
                  />
                </div>
                <div className="form-input-group">
                  <label>Map Embed URL</label>
                  <input
                    type="url"
                    value={contactData.mapUrl}
                    onChange={(e) => setContactData({ ...contactData, mapUrl: e.target.value })}
                    placeholder="https://maps.google.com/..."
                  />
                </div>
                <div className="form-input-group">
                  <label>Contact Banner Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setContactBannerFile, setContactBannerPreview)}
                  />
                  {contactBannerPreview && (
                    <div className="image-preview-frame">
                      <img src={contactBannerPreview} alt="Contact Banner" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-clock"></i>
                <h2>Office Hours</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label>Weekdays</label>
                  <input
                    type="text"
                    value={contactData.weekdayHours}
                    onChange={(e) => setContactData({ ...contactData, weekdayHours: e.target.value })}
                    placeholder="Mon-Fri: 9:00 AM - 6:00 PM"
                  />
                </div>
                <div className="form-input-group">
                  <label>Saturday</label>
                  <input
                    type="text"
                    value={contactData.saturdayHours}
                    onChange={(e) => setContactData({ ...contactData, saturdayHours: e.target.value })}
                    placeholder="Sat: 10:00 AM - 4:00 PM"
                  />
                </div>
                <div className="form-input-group">
                  <label>Sunday</label>
                  <input
                    type="text"
                    value={contactData.sundayHours}
                    onChange={(e) => setContactData({ ...contactData, sundayHours: e.target.value })}
                    placeholder="Closed"
                  />
                </div>
              </div>
            </div>

            <div className="settings-card-panel">
              <div className="panel-section-header">
                <i className="fas fa-headset"></i>
                <h2>Emergency Contacts</h2>
              </div>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label>Emergency Helpline</label>
                  <input
                    type="text"
                    value={contactData.emergencyPhone}
                    onChange={(e) => setContactData({ ...contactData, emergencyPhone: e.target.value })}
                    placeholder="+91 9876543210"
                  />
                </div>
                <div className="form-input-group">
                  <label>Ambulance Contact</label>
                  <input
                    type="text"
                    value={contactData.ambulanceContact}
                    onChange={(e) => setContactData({ ...contactData, ambulanceContact: e.target.value })}
                    placeholder="+91 9876543211"
                  />
                </div>
                <div className="form-input-group">
                  <label>Security Helpline</label>
                  <input
                    type="text"
                    value={contactData.securityContact}
                    onChange={(e) => setContactData({ ...contactData, securityContact: e.target.value })}
                    placeholder="+91 9876543212"
                  />
                </div>
              </div>
            </div>

            {isUploading && uploadProgress > 0 && (
              <div className="upload-progress">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}

            <div className="form-footer-actions-row">
              <button type="button" className="btn-row-action-reset" onClick={resetContact}>
                <i className="fas fa-undo"></i> Reset
              </button>
              <button type="submit" className="btn-action-trigger btn-blue" disabled={saving || isUploading}>
                <i className="fas fa-save"></i> {saving ? 'Saving...' : 'Save Contact Settings'}
              </button>
            </div>
          </form>
        </div>

        {/* Admin Management */}
        <div className={`tab-panel ${activeTab === 'admins' ? 'active' : ''}`}>
          <div className="settings-card-panel">
            <div className="panel-section-header">
              <i className="fas fa-user-plus"></i>
              <h2>Add New Administrator</h2>
            </div>
            <form onSubmit={addAdmin}>
              <div className="form-inputs-matrix">
                <div className="form-input-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={adminForm.name}
                    onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                    placeholder="Enter admin name"
                    required
                  />
                </div>
                <div className="form-input-group">
                  <label>Email Address <span className="required">*</span></label>
                  <input
                    type="email"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                    placeholder="admin@college.edu"
                    required
                  />
                </div>
                <div className="form-input-group">
                  <label>Password <span className="required">*</span></label>
                  <input
                    type="password"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    placeholder="Min 6 characters"
                    required
                  />
                </div>
                <div className="form-input-group">
                  <label>Confirm Password <span className="required">*</span></label>
                  <input
                    type="password"
                    value={adminForm.confirmPassword}
                    onChange={(e) => setAdminForm({ ...adminForm, confirmPassword: e.target.value })}
                    placeholder="Confirm password"
                    required
                  />
                </div>
                <div className="form-input-group">
                  <label>Role</label>
                  <select
                    value={adminForm.role}
                    onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value })}
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="editor">Editor</option>
                  </select>
                </div>
              </div>
              <div className="form-footer-actions-row">
                <button type="submit" className="btn-action-trigger btn-blue" disabled={saving}>
                  <i className="fas fa-plus-circle"></i> {saving ? 'Adding...' : 'Add Admin'}
                </button>
              </div>
            </form>
          </div>

          <div className="settings-card-panel">
            <div className="panel-section-header">
              <i className="fas fa-users"></i>
              <h2>Existing Administrators</h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        <i className="fas fa-users-slash" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }}></i>
                        No admins found
                      </td>
                    </tr>
                  ) : (
                    admins.map((admin) => (
                      <tr key={admin.id}>
                        <td><strong>{admin.name || 'N/A'}</strong></td>
                        <td>{admin.email}</td>
                        <td>
                          <span className="role-badge">
                            {admin.role || 'admin'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="delete-admin-btn"
                            onClick={() => deleteAdmin(admin.id)}
                          >
                            <i className="fas fa-trash"></i> Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;